'use client'

import { useMemo } from 'react'
import { uniqBy, isEqual } from 'lodash'

import { FilterModel } from '@marketplace-web/catalog/catalog-data'
import { initialFilterState, useCatalogContext } from '@marketplace-web/catalog/catalog-page-data'
import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'

import useFetchCatalogBrand from './useFetchCatalogBrand'
import useGetSelectedStaticFilters from './useGetSelectedStaticFilters'
import useFetchDynamicFilters from './useFetchDynamicFilters'

const useFilterReset = () => {
  const {
    state: { filters },
  } = useCatalogContext()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const { selectedStaticFilters } = useGetSelectedStaticFilters()

  const selectedStaticFiltersWithoutQuery = useMemo(() => {
    const names = uniqBy(selectedStaticFilters, 'name')
      .map(filter => filter.name)
      .filter(name => name !== 'query')

    if (
      filters.sortBy != null &&
      !isEqual(filters.sortBy, initialFilterState.sortBy) &&
      !names.includes('sortBy')
    ) {
      names.push('sortBy')
    }

    return names
  }, [selectedStaticFilters, filters.sortBy])

  const resetFilters = useLatestCallback(() => {
    const filtersToReset = selectedStaticFiltersWithoutQuery.reduce<Partial<FilterModel>>(
      (params, filterName) => {
        if (filterName in initialFilterState) {
          return {
            ...params,
            [filterName as keyof FilterModel]: initialFilterState[filterName as keyof FilterModel],
          }
        }

        return params
      },
      {},
    )

    fetchDynamicFilters({
      selectedDynamicFilters: [],
      selectedFilters: filtersToReset,
    })
    fetchCatalogBrand({ selectedDynamicFilters: [] })
  })

  return { resetFilters, selectedStaticFiltersWithoutQuery }
}

export default useFilterReset
